import { UNIT_SYSTEMS } from './units.js';

const DEG_TO_RAD = Math.PI / 180;

function toNumber(value) {
  const n = Number.parseFloat(value);
  return Number.isFinite(n) ? n : 0;
}

function isResultValid(value) {
  return Number.isFinite(value) && !Number.isNaN(value);
}

function sanitize(value) {
  return Number.isFinite(value) ? value : 0;
}

function tanDeg(value) {
  return Math.tan(value * DEG_TO_RAD);
}

function sinDeg(value) {
  return Math.sin(value * DEG_TO_RAD);
}

function cosDeg(value) {
  return Math.cos(value * DEG_TO_RAD);
}

function atanDeg(value) {
  return Math.atan(value) / DEG_TO_RAD;
}

function withNumeric(params, ids) {
  const mapped = {};
  for (const id of ids) {
    mapped[id] = toNumber(params[id]);
  }
  return mapped;
}

const jointCalculators = {
  surfacing(params) {
    const { B1, W } = withNumeric(params, ['B1', 'W']);
    return B1 * W;
  },

  square_groove(params) {
    const { T1, R, B1 } = withNumeric(params, ['T1', 'R', 'B1']);
    const MA1 = T1 * R;
    const CW = R * 2;
    const CT = atanDeg(B1 / (CW / 2)) * 2;
    let CR = CW / 2 / sinDeg(CT);
    CR = sanitize(CR);
    const CA = ((Math.PI * (CR * CR)) / 360) * (2 * CT) - (CR - B1) * (CW / 2);
    return MA1 + CA;
  },

  single_v(params) {
    const { T1, A1, R, L, B1 } = withNumeric(params, ['T1', 'A1', 'R', 'L', 'B1']);
    const tanHalf = tanDeg(A1 / 2);
    const MA1 = T1 * R;
    const MA2 = tanHalf * (T1 - L) * (T1 - L);
    const CW = tanHalf * (T1 - L) * 2 + R;
    const CT = atanDeg(B1 / (CW / 2)) * 2;
    let CR = CW / 2 / sinDeg(CT);
    CR = sanitize(CR);
    const CA = ((Math.PI * (CR * CR)) / 360) * (2 * CT) - (CR - B1) * (CW / 2);
    return MA1 + MA2 + CA;
  },

  single_bevel(params) {
    const { T1, A1, R, L, B1 } = withNumeric(params, ['T1', 'A1', 'R', 'L', 'B1']);
    const tanA1 = tanDeg(A1);
    const MA1 = T1 * R;
    const MA2 = tanA1 * (T1 - L) * (T1 - L) * 0.5;
    const CW = tanA1 * (T1 - L) + R;
    const CT = atanDeg(B1 / (CW / 2)) * 2;
    let CR = CW / 2 / sinDeg(CT);
    CR = sanitize(CR);
    const CA = ((Math.PI * (CR * CR)) / 360) * (2 * CT) - (CR - B1) * (CW / 2);
    return MA1 + MA2 + CA;
  },

  single_j(params) {
    const { T1, A1, R, L, B1, RD } = withNumeric(params, ['T1', 'A1', 'R', 'L', 'B1', 'RD']);
    const sinA1 = sinDeg(A1);
    const cosA1 = cosDeg(A1);
    const tanA1 = tanDeg(A1);
    const MA1 = T1 * R;
    const MA2 = ((Math.PI * (RD * RD)) / 360) * (90 - A1);
    const MA3 = cosA1 * RD * (T1 - RD - L);
    const MA4 = (sinA1 * RD + (T1 - RD - L)) * (tanA1 * (sinA1 * RD + (T1 - RD - L))) * 0.5;
    const MA5 = sinA1 * RD * (cosA1 * RD) * 0.5;
    const CW = tanA1 * (sinA1 * RD + (T1 - RD - L)) + cosA1 * RD + R;
    const CT = atanDeg(B1 / (CW / 2)) * 2;
    let CR = CW / 2 / sinDeg(CT);
    CR = sanitize(CR);
    const CA = ((Math.PI * (CR * CR)) / 360) * (2 * CT) - (CR - B1) * (CW / 2);
    return MA1 + MA2 + MA3 + MA4 + MA5 + CA;
  },

  single_u(params) {
    const { T1, A1, R, L, B1, RD } = withNumeric(params, ['T1', 'A1', 'R', 'L', 'B1', 'RD']);
    const halfA1 = A1 / 2;
    const sinHalf = sinDeg(halfA1);
    const cosHalf = cosDeg(halfA1);
    const tanHalf = tanDeg(halfA1);
    const MA1 = T1 * R;
    const MA2 = ((Math.PI * (RD * RD)) / 360) * (90 - halfA1) * 2;
    const MA3 = cosHalf * RD * (T1 - RD - L) * 2;
    const MA4 = (sinHalf * RD + (T1 - RD - L)) * (tanHalf * (sinHalf * RD + (T1 - RD - L)));
    const MA5 = sinHalf * RD * (cosHalf * RD);
    const CW = (tanHalf * (sinHalf * RD + (T1 - L - RD)) + cosHalf * RD) * 2 + R;
    const CT = atanDeg(B1 / (CW / 2)) * 2;
    let CR = CW / 2 / sinDeg(CT);
    CR = sanitize(CR);
    const CA = ((Math.PI * (CR * CR)) / 360) * (2 * CT) - (CR - B1) * (CW / 2);
    return MA1 + MA2 + MA3 + MA4 + MA5 + CA;
  },

  fillet(params, options = {}) {
    const { L1, L2 } = withNumeric(params, ['L1', 'L2']);
    let { FRE } = withNumeric(params, ['FRE']);
    const frePositive = options.frePositive !== false;
    if (!frePositive && FRE !== 0) {
      FRE = -FRE;
    }

    const MA = L1 * L2 * 0.5;
    const CW = Math.hypot(L1, L2);
    const CT = atanDeg(FRE / (CW / 2)) * 2;
    let CR = CW / 2 / sinDeg(CT);
    CR = sanitize(CR);
    const CA = ((Math.PI * (CR * CR)) / 360) * (2 * CT) - (CR - FRE) * (CW / 2);
    return MA + CA;
  },

  outside_corner(params) {
    const { A1, FRE, R, T1 } = withNumeric(params, ['A1', 'FRE', 'R', 'T1']);
    const MA1 = cosDeg(A1 / 2) * T1 * (sinDeg(A1 / 2) * T1);
    const MA2 = cosDeg(A1 / 2) * T1 * R;
    const CW = sinDeg(A1 / 2) * T1 * 2 + R;
    const CT = atanDeg(FRE / (CW / 2)) * 2;
    let CR = CW / 2 / sinDeg(CT);
    CR = sanitize(CR);
    const CA = ((Math.PI * (CR * CR)) / 360) * (2 * CT) - (CR - FRE) * (CW / 2);
    return MA1 + MA2 + CA;
  },
};

export function calculateCsa(jointId, params, options) {
  const calculator = jointCalculators[jointId];
  if (!calculator) {
    return Number.NaN;
  }
  const result = calculator(params, options);
  return isResultValid(result) ? result : Number.NaN;
}

export function calculateDepositionPerLength(csa, material, unitSystem) {
  if (!Number.isFinite(csa) || !material) {
    return Number.NaN;
  }
  if (unitSystem === UNIT_SYSTEMS.metric) {
    return (csa / 100000) * material.density_g_per_cm3;
  }
  return csa * 12 * material.density_lb_per_in3;
}

export function calculateFillerToPurchase(perLength, weldLength, efficiency, unitSystem) {
  if (
    !Number.isFinite(perLength) ||
    !Number.isFinite(weldLength) ||
    !Number.isFinite(efficiency) ||
    efficiency === 0
  ) {
    return Number.NaN;
  }
  if (unitSystem === UNIT_SYSTEMS.metric) {
    return (perLength * weldLength * 100) / efficiency;
  }
  return (perLength * weldLength) / efficiency;
}

export const jointKeys = Object.keys(jointCalculators);
